<template>
    <!-- Area > Zone > Point selector with search, expand/collapse and tri-state
         checkboxes. Modelled on the Mass Maintenance points picker, which is a
         far better fit for choosing many points than a single multi-select.

         Data-source agnostic: it takes a tree and emits the selected point ids,
         so any feature can hand it whatever tree it already has. -->
    <div class="overflow-clip rounded-md border border-gray-200 bg-white">
        <!-- On a phone the header stacks: the search takes its own full-width row,
             then Select all / Expand all share a 50/50 row below, each a full 48px
             (~8mm) tap target. On a wider screen (sm+) it collapses back to the
             original single inline row where a mouse points. -->
        <div class="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
            <input  v-model="searchQuery"
                    type="text"
                    :placeholder="searchPlaceholder"
                    class="search-input w-full min-h-[48px] rounded-md border border-gray-300 bg-white px-3 py-2 text-base transition-colors focus:border-blue-500 focus:ring-blue-500 sm:min-h-0 sm:flex-1 sm:text-sm"
            >
            <!-- Below sm the two buttons split their own row 50/50 (flex-1);
                 from sm up they return to their natural inline width. -->
            <div class="flex gap-2 sm:contents">
                <!-- Selecting everything is a common case, so it gets its own button
                     rather than making the user tick each area. Clearing lives on the
                     selection banner below, where it only appears once there is
                     something to clear. -->
                <button type="button"
                        :disabled="allSelected || visibleMeterIds.length === 0"
                        class="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white sm:min-h-0 sm:flex-none"
                        @click="selectAllVisible"
                >
                    {{ searchQuery.trim() ? 'Select all matching' : 'Select all' }}
                </button>
                <button type="button"
                        class="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:min-h-0 sm:flex-none"
                        @click="toggleAllExpanded"
                >
                    {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                </button>
            </div>
        </div>

        <!-- Always occupies its row, even at zero selections. With v-if the bar
             appeared on the first click and pushed every row down, moving the
             checkbox out from under the cursor so it could not be toggled back
             off without re-aiming. -->
        <div    class="flex h-12 items-center justify-between gap-3 border-b border-gray-100 px-4 transition-colors"
                :class="selectedCount > 0 ? 'bg-blue-50' : 'bg-gray-50'"
        >
            <span   class="text-sm font-medium"
                    :class="selectedCount > 0 ? 'text-blue-900' : 'text-gray-500'">
                {{ selectedCount > 0
                    ? `${selectedCount} ${selectedCount === 1 ? 'point' : 'points'} selected`
                    : 'No points selected' }}
            </span>

            <!-- Full 48px (~8mm) tap target on a phone: the button fills the
                 banner height and gets side padding, with a negative right margin
                 so its text still lines up with the row's right edge. It shrinks
                 back to a compact inline link from sm up. -->
            <button v-if="selectedCount > 0"
                    type="button"
                    class="-mr-2 inline-flex min-h-[48px] items-center gap-1 rounded-md px-2 text-sm font-medium text-blue-700 transition-colors hover:text-blue-900 sm:mr-0 sm:min-h-0 sm:px-0"
                    @click="clearSelection"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                Clear
            </button>
        </div>

        <div    v-if="filteredTree.length === 0"
                class="flex min-h-64 items-center justify-center p-8">
            <div class="flex flex-col items-center justify-center space-y-0.5">
                <span class="lnr icon-inbox !text-4xl !text-gray-500 mb-2 inline-block w-8 text-center"></span>
                <span class="text-base font-medium">
                    {{ searchQuery.trim() ? 'No matching points' : 'No points available' }}
                </span>
                <span class="text-sm text-gray-500">
                    {{ searchQuery.trim() ? 'Try a different search term.' : 'No points are accessible for this report.' }}
                </span>
            </div>
        </div>

        <ul v-else
            class="m-0 max-h-96 list-none overflow-y-auto p-0 divide-y divide-gray-100">
            <template   v-for="(area, areaIdx) in filteredTree"
                        :key="`a-${area.id}`">
                <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                    <div    class="flex min-h-[48px] cursor-pointer select-none items-stretch gap-1 pl-1 pr-4 font-medium text-gray-900 hover:bg-gray-100 sm:min-h-0 sm:items-center sm:gap-2 sm:py-3 sm:pl-4"
                            role="button"
                            :aria-expanded="isExpanded('area', area.id)"
                            @click="toggleExpanded('area', area.id)"
                    >
                        <!-- Chevron gets its own 48px (~8mm) hit zone on a phone so
                             the expand toggle is a real tap target, not a 16px glyph;
                             it shrinks to the natural inline glyph from sm up. -->
                        <span class="flex min-h-[48px] w-11 shrink-0 items-center justify-center text-gray-400 sm:min-h-0 sm:w-auto">
                            <svg v-if="isExpanded('area', area.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6" /></svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                        </span>
                        <!-- The label fills the row height (self-stretch + min-h) so
                             the select tap area is the full 48px too, not just the
                             ~20px checkbox. -->
                        <label  class="m-0 flex min-h-[48px] flex-1 cursor-pointer items-center gap-2 sm:min-h-0"
                                @click.stop>
                            <input  type="checkbox"
                                    class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:h-4 sm:w-4"
                                    :checked="areaState(area) === 'all'"
                                    :indeterminate.prop="areaState(area) === 'some'"
                                    @change="toggleArea(area, $event.target.checked)"
                            >
                            {{ area.name }}
                            <span class="text-xs font-normal text-gray-500">({{ areaMeterCount(area) }})</span>
                        </label>
                    </div>
                </li>

                <template v-if="isExpanded('area', area.id)">
                    <template   v-for="zone in area.zones"
                                :key="`z-${zone.id}`">
                        <li class="bg-white">
                            <div    class="flex min-h-[48px] cursor-pointer select-none items-stretch gap-1 pl-6 pr-4 text-gray-800 hover:bg-gray-100 sm:min-h-0 sm:items-center sm:gap-2 sm:py-3 sm:pl-10"
                                    role="button"
                                    :aria-expanded="isExpanded('zone', zone.id)"
                                    @click="toggleExpanded('zone', zone.id)"
                            >
                                <span class="flex min-h-[48px] w-11 shrink-0 items-center justify-center text-gray-400 sm:min-h-0 sm:w-auto">
                                    <svg v-if="isExpanded('zone', zone.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6" /></svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                                </span>
                                <label  class="m-0 flex min-h-[48px] flex-1 cursor-pointer items-center gap-2 sm:min-h-0"
                                        @click.stop>
                                    <input  type="checkbox"
                                            class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:h-4 sm:w-4"
                                            :checked="zoneState(zone) === 'all'"
                                            :indeterminate.prop="zoneState(zone) === 'some'"
                                            @change="toggleZone(zone, $event.target.checked)"
                                    >
                                    {{ zone.name }}
                                    <span class="text-xs font-normal text-gray-500">({{ zone.meters.length }})</span>
                                </label>
                            </div>
                        </li>

                        <!-- v-if wraps the v-for rather than sharing an element,
                             per the Vue styleguide: the two never belong on the
                             same tag. Mirrors the area/zone nesting above. -->
                        <template v-if="isExpanded('zone', zone.id)">
                            <li v-for="meter in zone.meters"
                                :key="`m-${meter.id}`"
                                class="bg-white"
                            >
                                <!-- Indented past the zone's chevron+checkbox so the
                                     point clearly nests under its zone on mobile too;
                                     the deeper desktop indent returns from sm up. -->
                                <label class="m-0 flex min-h-[48px] cursor-pointer items-center gap-2 pl-[4.5rem] pr-4 text-sm text-gray-700 hover:bg-gray-100 sm:min-h-0 sm:py-2.5 sm:pl-16">
                                    <input  type="checkbox"
                                            class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:h-4 sm:w-4"
                                            :checked="isSelected(meter.id)"
                                            @change="toggleMeter(meter.id, $event.target.checked)"
                                    >
                                    {{ meter.name }}
                                </label>
                            </li>
                        </template>
                    </template>
                </template>
            </template>
        </ul>
    </div>
</template>

<script>
import _ from "lodash"

export default {
    name: "PointSelectTree",
    emits: ['update:modelValue'],
    props: {
        // Selected point ids.
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        // [{ id, name, zones: [{ id, name, meters: [{ id, name }] }] }]
        tree: {
            type: Array,
            default: () => ([]),
        },
        searchPlaceholder: {
            type: String,
            default: 'Search areas, zones, or points',
        },
        // Expand every area/zone on first render.
        expandedByDefault: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            searchQuery: '',
            collapsed: {area: {}, zone: {}},
        }
    },
    computed: {
        selected() {
            return this.modelValue ?? []
        },
        selectedCount() {
            return this.selected.length
        },
        // Filter to matching areas/zones/points. An area or zone that matches by
        // name keeps all of its children.
        filteredTree() {
            const query = this.searchQuery.trim().toLowerCase()
            if (query === '') {
                return this.tree
            }

            const matches = (value) => (value ?? '').toLowerCase().indexOf(query) !== -1

            return this.tree
                .map((area) => {
                    if (matches(area.name)) {
                        return area
                    }

                    const zones = area.zones
                        .map((zone) => {
                            if (matches(zone.name)) {
                                return zone
                            }

                            const meters = zone.meters.filter((meter) => matches(meter.name))
                            return meters.length > 0 ? {...zone, meters} : null
                        })
                        .filter(Boolean)

                    return zones.length > 0 ? {...area, zones} : null
                })
                .filter(Boolean)
        },
        // Only what is currently visible, so "Select all" respects an active
        // search rather than silently selecting filtered-out points.
        visibleMeterIds() {
            return this.filteredTree.flatMap((area) => this.meterIdsIn(area))
        },
        allSelected() {
            return this.visibleMeterIds.length > 0
                && this.visibleMeterIds.every((id) => this.isSelected(id))
        },
        allExpanded() {
            // Expanded unless explicitly collapsed, so newly loaded data starts open
            // when expandedByDefault is set.
            return this.filteredTree.every((area) => this.isExpanded('area', area.id))
        },
    },
    methods: {
        isExpanded(type, id) {
            const explicit = this.collapsed[type][id]
            return explicit === undefined ? this.expandedByDefault : explicit
        },
        toggleExpanded(type, id) {
            this.collapsed[type] = {...this.collapsed[type], [id]: ! this.isExpanded(type, id)}
        },
        toggleAllExpanded() {
            const expand = ! this.allExpanded
            const areas = {}
            const zones = {}

            this.tree.forEach((area) => {
                areas[area.id] = expand
                area.zones.forEach((zone) => {
                    zones[zone.id] = expand
                })
            })

            this.collapsed = {area: areas, zone: zones}
        },
        meterIdsIn(node) {
            return node.zones
                ? node.zones.flatMap((zone) => zone.meters.map((meter) => meter.id))
                : node.meters.map((meter) => meter.id)
        },
        isSelected(meterId) {
            return this.selected.indexOf(meterId) !== -1
        },
        selectionState(ids) {
            if (ids.length === 0) {
                return 'none'
            }

            const count = ids.filter((id) => this.isSelected(id)).length
            if (count === 0) {
                return 'none'
            }

            return count === ids.length ? 'all' : 'some'
        },
        areaState(area) {
            return this.selectionState(this.meterIdsIn(area))
        },
        zoneState(zone) {
            return this.selectionState(this.meterIdsIn(zone))
        },
        areaMeterCount(area) {
            return this.meterIdsIn(area).length
        },
        emitSelection(ids) {
            this.$emit('update:modelValue', _.uniq(ids))
        },
        setSelected(ids, selected) {
            this.emitSelection(selected
                ? [...this.selected, ...ids]
                : this.selected.filter((id) => ids.indexOf(id) === -1))
        },
        toggleMeter(meterId, selected) {
            this.setSelected([meterId], selected)
        },
        toggleZone(zone, selected) {
            this.setSelected(this.meterIdsIn(zone), selected)
        },
        toggleArea(area, selected) {
            this.setSelected(this.meterIdsIn(area), selected)
        },
        selectAllVisible() {
            this.setSelected(this.visibleMeterIds, true)
        },
        clearSelection() {
            this.emitSelection([])
        },
    },
}
</script>

<style scoped>
/*
 * Stop iOS Safari auto-zooming when the search field is focused on a phone.
 * Safari zooms any input whose font is under 16px. The Tailwind `text-base`
 * on the input is 1rem, but this app sets `html { font-size: 14px }`, so 1rem
 * resolves to 14px -- under the threshold -- and Safari zooms. Pin an explicit
 * 16px (a hard pixel value, not rem) below sm so it is exactly at the threshold
 * regardless of the root size. Desktop keeps the smaller Tailwind size.
 */
@media (max-width: 639px) {
    .search-input {
        font-size: 16px;
    }
}
</style>
